import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { apiService } from '../services/api.js'
import config from '../config/config.js'
import apiCallManager from '../utils/apiCallManager.js'

const IMPERSONATION_STORAGE_KEY = 'neat_fit_impersonation_context'

// Default landing route after login, based on the user's role.
export const getDefaultRouteForUser = (user) => {
  const roleName = user?.role?.name || (typeof user?.role === 'string' ? user.role : '')
  const isCompanyAdmin = roleName === 'Company Admin'
  return isCompanyAdmin ? '/company-clients' : '/dashboard'
}

export const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      companies: [],
      selectedCompany: null,
      sideMenuItems: [],
      companyDetails: null,
      companyUrl: null,
      isAuthenticated: false,
      isLoading: false,
      isLoadingCompanies: false,
      isLoadingSideMenu: false,
      isLoadingCompanyDetails: false,
      isAuthenticating: false, // New state for post-login loading
      isImpersonating: false,
      impersonationMeta: null,
      error: null,
      authStep: 'email', // 'email', 'company', 'password'

      // Actions

      clearSessionScopedData: async () => {
        apiCallManager.cancelAllRequests()
        apiCallManager.clearDebouncedRequests()

        const clearCalls = []
        try {
          const { useUserStore } = await import('./userStore.js')
          clearCalls.push(useUserStore.getState().clearUserData?.())
        } catch (error) {
          console.warn('Failed to clear user store:', error.message)
        }

        try {
          const { useExerciseStore } = await import('./exerciseStore.js')
          clearCalls.push(useExerciseStore.getState().clearAllData?.())
        } catch (error) {
          console.warn('Failed to clear exercise store:', error.message)
        }

        try {
          const { useNutritionStore } = await import('./nutritionStore.js')
          clearCalls.push(useNutritionStore.getState().resetUserScopedData?.())
        } catch (error) {
          console.warn('Failed to clear nutrition store:', error.message)
        }

        try {
          const { useCompositionStore } = await import('./compositionStore.js')
          clearCalls.push(useCompositionStore.getState().clearAllData?.())
        } catch (error) {
          console.warn('Failed to clear composition store:', error.message)
        }

        try {
          const { useDashboardStore } = await import('./dashboardStore.js')
          clearCalls.push(useDashboardStore.getState().clearDashboardData?.())
        } catch (error) {
          console.warn('Failed to clear dashboard store:', error.message)
        }

        await Promise.all(clearCalls.filter(Boolean))
      },
      
      // Step 1: Fetch companies for email
      fetchUserCompanies: async (email) => {
        set({ isLoadingCompanies: true, error: null })
        
        try {
          const result = await apiService.fetchUserCompanies(email)
          
          if (result.success) {
            const companies = result.data
            
            if (companies.length === 1) {
              // Auto-select if only one company
              set({ 
                companies,
                selectedCompany: companies[0],
                authStep: 'password',
                isLoadingCompanies: false 
              })
            } else if (companies.length > 1) {
              // Show company selection
              set({ 
                companies,
                authStep: 'company',
                isLoadingCompanies: false 
              })
            } else {
              // No companies found
              set({ 
                error: 'No companies found for this email address',
                isLoadingCompanies: false 
              })
              return { success: false, error: 'No companies found for this email address' }
            }
            
            return { success: true, companies }
          } else {
            set({ 
              error: result.error,
              isLoadingCompanies: false 
            })
            return { success: false, error: result.error }
          }
        } catch (error) {
          const errorMessage = error.message || 'Failed to fetch companies'
          set({ 
            error: errorMessage,
            isLoadingCompanies: false 
          })
          return { success: false, error: errorMessage }
        }
      },

      // Step 2: Select company (if multiple)
      selectCompany: (company) => {

        set({ 
          selectedCompany: company,
          authStep: 'password',
          error: null 
        })
      },

      // Step 3: Login with password
      login: async (email, password, companyId) => {
        set({ isLoading: true, error: null })
        
        try {
          await get().clearSessionScopedData()
          const credentials = {
            email,
            password,
            company_id: companyId || get().selectedCompany?.company?.id
          }

          const result = await apiService.login(credentials)
          
          if (result.success) {
            const authData = result.data
            
            // Set authenticating state to show loading during post-login setup
            set({ 
              isLoading: false,
              isAuthenticating: true,
              error: null
            })
            
            // Store tokens
            localStorage.setItem(config.storage.authToken, authData.accessToken)
            localStorage.setItem(config.storage.refreshToken, authData.refreshToken)
            localStorage.setItem(config.storage.userData, JSON.stringify(authData.userData))
            localStorage.setItem(config.storage.companyData, JSON.stringify(get().selectedCompany))
            
            // Extract company URL from auth response
            const companyUrl =
              authData?.userData?.company_url ||
              get().selectedCompany?.company?.company_url ||
              get().selectedCompany?.company_url ||
              ''
            
            // Update store with auth data
            set({ 
              user: authData.userData,
              companyUrl,
              isAuthenticated: true,
              isImpersonating: false,
              impersonationMeta: null,
              authStep: 'email' // Reset for next login
            })
            localStorage.removeItem(IMPERSONATION_STORAGE_KEY)

            // Fetch side menu and company details after successful authentication
            if (companyUrl) {
              await get().fetchSideMenu(companyUrl)
              await get().fetchCompanyDetails(companyUrl)
              
              // Fetch states data after successful login
              try {
                // Import userStore dynamically to avoid circular dependency
                const { useUserStore } = await import('./userStore.js')
                const userStore = useUserStore.getState()
                await userStore.fetchStates(companyUrl)
              } catch (error) {
                console.warn('⚠️ Failed to fetch states:', error.message)
              }
            }

            // Clear authenticating state after all setup is complete
            set({ isAuthenticating: false })

            return { success: true, data: authData }
          } else {
            set({ 
              error: result.error,
              isLoading: false,
              isAuthenticating: false 
            })
            return { success: false, error: result.error }
          }
        } catch (error) {
          const errorMessage = error.message || 'Login failed'
          set({ 
            error: errorMessage,
            isLoading: false,
            isAuthenticating: false 
          })
          return { success: false, error: errorMessage }
        }
      },

      googleLogin: async (token, companyId) => {
        const resolvedCompanyId = companyId || get().selectedCompany?.company?.id || null

        set({ isLoading: true, error: null })

        try {
          await get().clearSessionScopedData()
          const payload = {
            token,
            company_id: resolvedCompanyId
          }

          const result = await apiService.loginWithGoogle(payload)

          if (result.success) {
            const authData = result.data

            set({
              isLoading: false,
              isAuthenticating: true,
              error: null
            })

            localStorage.setItem(config.storage.authToken, authData.accessToken)
            localStorage.setItem(config.storage.refreshToken, authData.refreshToken)
            localStorage.setItem(config.storage.userData, JSON.stringify(authData.userData))

            const companyDataToStore = get().selectedCompany || null
            if (companyDataToStore) {
              localStorage.setItem(config.storage.companyData, JSON.stringify(companyDataToStore))
            } else {
              localStorage.removeItem(config.storage.companyData)
            }

            const companyUrl =
              authData?.userData?.company_url ||
              companyDataToStore?.company?.company_url ||
              companyDataToStore?.company_url ||
              ''

            set({
              user: authData.userData,
              companyUrl,
              isAuthenticated: true,
              isImpersonating: false,
              impersonationMeta: null,
              authStep: 'email'
            })
            localStorage.removeItem(IMPERSONATION_STORAGE_KEY)

            if (companyUrl) {
              await get().fetchSideMenu(companyUrl)
              await get().fetchCompanyDetails(companyUrl)

              try {
                const { useUserStore } = await import('./userStore.js')
                const userStore = useUserStore.getState()
                await userStore.fetchStates(companyUrl)
              } catch (error) {
                console.warn('⚠️ Failed to fetch states:', error.message)
              }
            }

            set({ isAuthenticating: false })

            return { success: true, data: authData }
          } else {
            set({
              error: result.error,
              isLoading: false,
              isAuthenticating: false
            })
            return { success: false, error: result.error }
          }
        } catch (error) {
          const errorMessage = error.message || 'Google login failed'
          set({
            error: errorMessage,
            isLoading: false,
            isAuthenticating: false
          })
          return { success: false, error: errorMessage }
        }
      },

      // Fetch side menu items
      fetchSideMenu: async (companyUrl) => {
        set({ isLoadingSideMenu: true })
        
        try {
          const result = await apiService.fetchSideMenu(companyUrl)
          
          if (result.success) {
            set({ 
              sideMenuItems: result.data,
              isLoadingSideMenu: false 
            })
            
            // Store side menu in localStorage for persistence
            localStorage.setItem(config.storage.sideMenuData, JSON.stringify(result.data))
            
            return { success: true, data: result.data }
          } else {
            set({ 
              error: result.error,
              isLoadingSideMenu: false 
            })
            return { success: false, error: result.error }
          }
        } catch (error) {
          const errorMessage = error.message || 'Failed to fetch side menu'
          set({ 
            error: errorMessage,
            isLoadingSideMenu: false 
          })
          return { success: false, error: errorMessage }
        }
      },

      // Fetch company details
      fetchCompanyDetails: async (companyUrl) => {
        set({ isLoadingCompanyDetails: true })
        
        try {
          const result = await apiService.fetchCompanyDetails(companyUrl)
          
          if (result.success) {
            // Handle array response - take the first company details object
            const companyDetailsData = Array.isArray(result.data) ? result.data[0] : result.data
            
            set({ 
              companyDetails: companyDetailsData,
              isLoadingCompanyDetails: false 
            })
            
            // Store company details in localStorage for persistence
            localStorage.setItem(config.storage.companyDetailsData, JSON.stringify(companyDetailsData))
            
            return { success: true, data: result.data }
          } else {
            set({ 
              error: result.error,
              isLoadingCompanyDetails: false 
            })
            return { success: false, error: result.error }
          }
        } catch (error) {
          const errorMessage = error.message || 'Failed to fetch company details'
          set({ 
            error: errorMessage,
            isLoadingCompanyDetails: false 
          })
          return { success: false, error: errorMessage }
        }
      },

      // Demo login for testing
      demoLogin: async () => {
        set({ isAuthenticating: true })
        
        const demoEmail = 'pratap.consultant@filos.com'
        const demoPassword = 'thf123456'
        
        try {
          // First fetch companies
          const companiesResult = await get().fetchUserCompanies(demoEmail)
          if (companiesResult.success) {
            // Then login
            return await get().login(demoEmail, demoPassword)
          }
          set({ isAuthenticating: false })
          return companiesResult
        } catch (error) {
          set({ isAuthenticating: false })
          return { success: false, error: error.message }
        }
      },

      startImpersonation: async (clientId, returnPath = '/consultant-clients') => {
        const { companyUrl, selectedCompany, sideMenuItems, companyDetails, user } = get()
        if (!companyUrl || !clientId) {
          return { success: false, error: 'Missing company or client context for impersonation' }
        }

        set({ isAuthenticating: true, error: null })

        // The snapshot of the consultant session is written before the request. If the request
        // fails it must be removed again, otherwise a reload would show "viewing as client"
        // while the consultant's own token is still active.
        let savedSnapshot = false
        const discardSnapshot = () => {
          if (savedSnapshot) localStorage.removeItem(IMPERSONATION_STORAGE_KEY)
        }

        try {
          await get().clearSessionScopedData()
          const existingContextRaw = localStorage.getItem(IMPERSONATION_STORAGE_KEY)
          if (!existingContextRaw) {
            const originalContext = {
              authToken: localStorage.getItem(config.storage.authToken),
              refreshToken: localStorage.getItem(config.storage.refreshToken),
              userData: localStorage.getItem(config.storage.userData),
              companyData: localStorage.getItem(config.storage.companyData),
              sideMenuData: localStorage.getItem(config.storage.sideMenuData),
              companyDetailsData: localStorage.getItem(config.storage.companyDetailsData),
              companyUrl,
              returnPath,
              savedAt: Date.now(),
            }
            localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(originalContext))
            savedSnapshot = true
          }

          const result = await apiService.impersonateClient(companyUrl, clientId)
          if (!result.success) {
            discardSnapshot()
            set({ isAuthenticating: false })
            return { success: false, error: result.error || 'Failed to impersonate user' }
          }

          const authData = result.data || {}
          const impersonatedUser = authData.userData || null
          if (!authData.accessToken || !impersonatedUser) {
            discardSnapshot()
            set({ isAuthenticating: false })
            return { success: false, error: 'Impersonation response is missing authentication details' }
          }
          const nextCompanyUrl =
            impersonatedUser?.company_url ||
            user?.company_url ||
            selectedCompany?.company?.company_url ||
            selectedCompany?.company_url ||
            companyUrl

          localStorage.setItem(config.storage.authToken, authData.accessToken)
          if (authData.refreshToken) {
            localStorage.setItem(config.storage.refreshToken, authData.refreshToken)
          } else {
            localStorage.removeItem(config.storage.refreshToken)
          }
          localStorage.setItem(config.storage.userData, JSON.stringify(impersonatedUser))
          if (authData.companyAdminId) {
            localStorage.setItem('neat_fit_impersonation_company_admin_id', authData.companyAdminId)
          }

          const impersonationMeta = {
            clientId: String(clientId),
            clientName: [impersonatedUser?.firstname, impersonatedUser?.lastname].filter(Boolean).join(' ').trim(),
            clientEmail: impersonatedUser?.email || '',
            returnPath
          }

          set({
            user: impersonatedUser,
            companyUrl: nextCompanyUrl,
            sideMenuItems,
            companyDetails,
            isAuthenticated: true,
            isImpersonating: true,
            impersonationMeta
          })

          if (nextCompanyUrl) {
            await Promise.all([
              get().fetchSideMenu(nextCompanyUrl),
              get().fetchCompanyDetails(nextCompanyUrl)
            ])

            try {
              const { useUserStore } = await import('./userStore.js')
              const userStore = useUserStore.getState()
              await Promise.all([
                userStore.fetchUserProfile(nextCompanyUrl),
                userStore.fetchStates(nextCompanyUrl)
              ])
            } catch (error) {
              console.warn('Failed to fetch user data after impersonation:', error.message)
            }
          }

          set({ isAuthenticating: false })
          return { success: true, data: authData, meta: impersonationMeta }
        } catch (error) {
          discardSnapshot()
          set({ isAuthenticating: false })
          return { success: false, error: error.message || 'Failed to impersonate user' }
        }
      },

      exitImpersonation: async () => {
        const contextRaw = localStorage.getItem(IMPERSONATION_STORAGE_KEY)
        if (!contextRaw) {
          return { success: false, error: 'No impersonation session found' }
        }

        set({ isAuthenticating: true, error: null })

        try {
          await get().clearSessionScopedData()
          const context = JSON.parse(contextRaw)
          const {
            authToken,
            refreshToken,
            userData,
            companyData,
            sideMenuData,
            companyDetailsData,
            companyUrl: savedCompanyUrl,
            returnPath
          } = context || {}

          if (!authToken || !userData) {
            localStorage.removeItem(IMPERSONATION_STORAGE_KEY)
            set({ isAuthenticating: false, isImpersonating: false, impersonationMeta: null })
            return { success: false, error: 'Original consultant session not available' }
          }

          localStorage.setItem(config.storage.authToken, authToken)
          if (refreshToken) localStorage.setItem(config.storage.refreshToken, refreshToken)
          localStorage.setItem(config.storage.userData, userData)
          if (companyData) localStorage.setItem(config.storage.companyData, companyData)
          if (sideMenuData) localStorage.setItem(config.storage.sideMenuData, sideMenuData)
          if (companyDetailsData) localStorage.setItem(config.storage.companyDetailsData, companyDetailsData)
          localStorage.removeItem(IMPERSONATION_STORAGE_KEY)
          localStorage.removeItem('neat_fit_impersonation_company_admin_id')

          const restoredUser = JSON.parse(userData)
          const restoredCompany = companyData ? JSON.parse(companyData) : null
          const restoredSideMenu = sideMenuData ? JSON.parse(sideMenuData) : []
          const restoredCompanyDetails = companyDetailsData ? JSON.parse(companyDetailsData) : null
          const restoredCompanyUrl =
            restoredUser?.company_url ||
            restoredCompany?.company?.company_url ||
            restoredCompany?.company_url ||
            savedCompanyUrl ||
            ''

          set({
            user: restoredUser,
            selectedCompany: restoredCompany,
            sideMenuItems: restoredSideMenu,
            companyDetails: restoredCompanyDetails,
            companyUrl: restoredCompanyUrl,
            isAuthenticated: true,
            isImpersonating: false,
            impersonationMeta: null
          })

          try {
            const { useUserStore } = await import('./userStore.js')
            const userStore = useUserStore.getState()
            if (restoredCompanyUrl) {
              await Promise.all([
                userStore.fetchUserProfile(restoredCompanyUrl),
                userStore.fetchStates(restoredCompanyUrl)
              ])
            }
          } catch (error) {
            console.warn('Failed to refresh user data after exiting impersonation:', error.message)
          }

          set({ isAuthenticating: false })
          return { success: true, returnPath: returnPath || '/consultant-clients' }
        } catch (error) {
          set({ isAuthenticating: false })
          return { success: false, error: error.message || 'Failed to exit impersonation' }
        }
      },

      logout: () => {
        get().clearSessionScopedData()
        // Clear localStorage
        localStorage.removeItem(config.storage.authToken)
        localStorage.removeItem(config.storage.refreshToken)
        localStorage.removeItem(config.storage.userData)
        localStorage.removeItem(config.storage.companyData)
        localStorage.removeItem(config.storage.sideMenuData)
        localStorage.removeItem(config.storage.companyDetailsData)
        localStorage.removeItem(IMPERSONATION_STORAGE_KEY)
        localStorage.removeItem('neat_fit_impersonation_company_admin_id')
        
        // Clear userStore data
        try {
          // Import userStore dynamically to avoid circular dependency
          import('./userStore.js').then(({ useUserStore }) => {
            const userStore = useUserStore.getState()
            userStore.clearUserData()
          })
        } catch (error) {
          console.warn('⚠️ Failed to clear user store:', error.message)
        }
        
        // Reset store
        set({ 
          user: null,
          companies: [],
          selectedCompany: null,
          sideMenuItems: [],
          companyDetails: null,
          companyUrl: null,
          isAuthenticated: false,
          isImpersonating: false,
          impersonationMeta: null,
          authStep: 'email',
          error: null 
        })
      },

      // Reset auth flow
      resetAuthFlow: () => {
        set({
          companies: [],
          selectedCompany: null,
          authStep: 'email',
          error: null,
          isLoadingCompanies: false,
          isLoading: false,
          isLoadingSideMenu: false,
          isLoadingCompanyDetails: false,
          isAuthenticating: false
        })
      },

      updateProfile: (updates) => {
        const currentUser = get().user
        if (currentUser) {
          const updatedUser = { ...currentUser, ...updates }
          set({ user: updatedUser })
          localStorage.setItem(config.storage.userData, JSON.stringify(updatedUser))
        }
      },

      clearError: () => {
        set({ error: null })
      },

      // Password reset
      resetPassword: async (email) => {
        set({ isLoading: true, error: null })
        
        try {
          // TODO: Implement actual password reset API call
          await new Promise(resolve => setTimeout(resolve, 1000))
          
          set({ isLoading: false })
          return { success: true }
        } catch (error) {
          set({ 
            error: error.message, 
            isLoading: false 
          })
          return { success: false, error: error.message }
        }
      },

      // Initialize auth from localStorage
      initializeAuth: async () => {
        try {
          const token = localStorage.getItem(config.storage.authToken)
          const userData = localStorage.getItem(config.storage.userData)
          const sideMenuData = localStorage.getItem(config.storage.sideMenuData)
          const companyData = localStorage.getItem(config.storage.companyData)
          const companyDetailsData = localStorage.getItem(config.storage.companyDetailsData)
          const impersonationContextRaw = localStorage.getItem(IMPERSONATION_STORAGE_KEY)
          
          if (token && userData) {
            const user = JSON.parse(userData)
            const sideMenuItems = sideMenuData ? JSON.parse(sideMenuData) : []
            const selectedCompany = companyData ? JSON.parse(companyData) : null
            const companyDetails = companyDetailsData ? JSON.parse(companyDetailsData) : null
            
            // Extract company URL from user data
            const companyUrl =
              user?.company_url ||
              selectedCompany?.company?.company_url ||
              selectedCompany?.company_url ||
              ''
            set({ 
              user,
              sideMenuItems,
              selectedCompany,
              companyDetails,
              companyUrl,
              isAuthenticated: true,
              isImpersonating: Boolean(impersonationContextRaw),
              impersonationMeta: (() => {
                if (!impersonationContextRaw) return null
                try {
                  const parsed = JSON.parse(impersonationContextRaw)
                  return {
                    clientId: user?.id != null ? String(user.id) : '',
                    clientName: [user?.firstname, user?.lastname].filter(Boolean).join(' ').trim(),
                    clientEmail: user?.email || '',
                    returnPath: parsed?.returnPath || '/consultant-clients'
                  }
                } catch {
                  return null
                }
              })()
            })

            // Fetch user profile and states after restoring auth
            if (companyUrl) {
              try {
                // Import userStore dynamically to avoid circular dependency
                const { useUserStore } = await import('./userStore.js')
                const userStore = useUserStore.getState()
                
                // Fetch user profile and states
                await Promise.all([
                  userStore.fetchUserProfile(companyUrl),
                  userStore.fetchStates(companyUrl)
                ])
              } catch (error) {
                console.warn('⚠️ Failed to fetch user data after auth restoration:', error.message)
              }
            }
          }
        } catch (error) {
          console.error('Failed to initialize auth:', error)
          get().logout()
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        user: state.user, 
        isAuthenticated: state.isAuthenticated,
        isImpersonating: state.isImpersonating,
        impersonationMeta: state.impersonationMeta,
        companies: state.companies,
        selectedCompany: state.selectedCompany,
        sideMenuItems: state.sideMenuItems,
        companyDetails: state.companyDetails,
        companyUrl: state.companyUrl
      }),
    }
  )
)
